import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenants } from '@/presentation/hooks/useTenants';
import { usePlans } from '@/presentation/hooks/usePlans';
import { useToast } from '@/presentation/contexts/ToastContext';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { ChangeTenantStatusAction } from '@/application/actions';
import { Button, Input, ConfirmModal, ActionMenu } from '@/presentation/components/common';
import type { TenantGridDTO } from '@/domain/repositories/ITenantRepository';
import { CreateTenantModal } from '@/presentation/components/tenants/CreateTenantModal';
import { EditTenantModal } from '@/presentation/components/tenants/EditTenantModal';
import { PaymentHistoryModal } from '@/presentation/components/tenants/PaymentHistoryModal';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type SortField = 'id' | 'nombre' | 'emailAdministrador' | 'activo' | 'planAdminId' | 'fechaActivoUTC';
type SortDirection = 'asc' | 'desc';

export const TenantsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenants, isLoading, error, refetch } = useTenants();
  const { plans } = usePlans();
  const { success, error: showError } = useToast();
  const { canAdminister } = useAuth();
  
  // Estados de filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [emailFilter, setEmailFilter] = useState('');
  const [expiryFilter, setExpiryFilter] = useState<'all' | 'expired' | 'expires_soon' | 'expires_next_month'>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);  
  // Estados de ordenamiento
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Estados de modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantGridDTO | null>(null);
  const [historyTenant, setHistoryTenant] = useState<TenantGridDTO | null>(null);
  const [statusChangeTenant, setStatusChangeTenant] = useState<TenantGridDTO | null>(null);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [notifySuspension, setNotifySuspension] = useState(true);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearchTerm(q);
  }, [searchParams]);

  // Función para obtener nombre del plan
  const getPlanName = (planId?: number): string => {
    if (!planId) return '-';
    const plan = plans.find(p => p.id === planId);
    return plan?.nombre || '-';
  };

  // Función para calcular días hasta vencimiento
  const getDaysUntilExpiry = (fechaVencimiento?: string): number | null => {
    if (!fechaVencimiento) return null;
    const expiry = new Date(fechaVencimiento);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filtrar tenants
  const filteredTenants = useMemo(() => {
    let filtered = [...tenants];

    // Búsqueda general
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tenant =>
        tenant.nombre.toLowerCase().includes(term) ||
        tenant.emailAdministrador.toLowerCase().includes(term) ||
        tenant.id.toString().includes(term)
      );
    }

    // Filtro por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(tenant =>
        statusFilter === 'active' ? tenant.activo : !tenant.activo
      );
    }

    // Filtro por email
    if (emailFilter) {
      const term = emailFilter.toLowerCase();
      filtered = filtered.filter(tenant =>
        tenant.emailAdministrador.toLowerCase().includes(term)
      );
    }

    // Filtro por vencimiento
    if (expiryFilter !== 'all') {
      filtered = filtered.filter(tenant => {
        if (!tenant.fechaVencimiento) return false;
        const days = getDaysUntilExpiry(tenant.fechaVencimiento);
        if (days === null) return false;
        
        if (expiryFilter === 'expired') return days < 0;
        if (expiryFilter === 'expires_soon') return days >= 0 && days <= 14;
        if (expiryFilter === 'expires_next_month') {
          const expiry = new Date(tenant.fechaVencimiento);
          const nextMonth = new Date();
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          return expiry.getMonth() === nextMonth.getMonth() && 
                 expiry.getFullYear() === nextMonth.getFullYear();
        }
        return true;
      });
    }

    // Ordenamiento
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'nombre':
          aValue = a.nombre.toLowerCase();
          bValue = b.nombre.toLowerCase();
          break;
        case 'emailAdministrador':
          aValue = a.emailAdministrador.toLowerCase();
          bValue = b.emailAdministrador.toLowerCase();
          break;
        case 'activo':
          aValue = a.activo ? 1 : 0;
          bValue = b.activo ? 1 : 0;
          break;
        case 'planAdminId':
          aValue = a.planAdminId || 0;
          bValue = b.planAdminId || 0;
          break;
        case 'fechaActivoUTC':
          aValue = new Date(a.fechaActivoUTC).getTime();
          bValue = new Date(b.fechaActivoUTC).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [tenants, searchTerm, statusFilter, emailFilter, expiryFilter, sortField, sortDirection]);

  // Manejar click en encabezado de columna para ordenar
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Función para obtener clase de badge de estado Activo/Inactivo
  const getActivoStatusBadgeClass = (activo: boolean) => {
    if (activo) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  // Función para limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setEmailFilter('');
    setExpiryFilter('all');
  };

  const getStatusFilterLabel = (value: typeof statusFilter): string => {
    if (value === 'active') return t('pages.tenants.active');
    if (value === 'inactive') return t('pages.tenants.inactive');
    return t('pages.tenants.all');
  };

  const getExpiryFilterLabel = (value: typeof expiryFilter): string => {
    if (value === 'expired') return t('pages.tenants.expired');
    if (value === 'expires_soon') return t('pages.tenants.expiresSoon');
    if (value === 'expires_next_month') return t('pages.tenants.expiresNextMonth');
    return t('pages.tenants.all');
  };

  const activeFilterChips = [
    ...(searchTerm.trim()
      ? [{
          key: 'search',
          label: `${t('common.search')}: ${searchTerm.trim()}`,
          onRemove: () => setSearchTerm(''),
        }]
      : []),
    ...(statusFilter !== 'all'
      ? [{
          key: 'status',
          label: `${t('pages.tenants.status')}: ${getStatusFilterLabel(statusFilter)}`,
          onRemove: () => setStatusFilter('all'),
        }]
      : []),
    ...(emailFilter.trim()
      ? [{
          key: 'email',
          label: `${t('pages.tenants.email')}: ${emailFilter.trim()}`,
          onRemove: () => setEmailFilter(''),
        }]
      : []),
    ...(expiryFilter !== 'all'
      ? [{
          key: 'expiry',
          label: `${t('pages.tenants.expiration')}: ${getExpiryFilterLabel(expiryFilter)}`,
          onRemove: () => setExpiryFilter('all'),
        }]
      : []),
  ];

  // Renderizar icono de ordenamiento
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  if (isLoading && tenants.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-page h-full min-w-0 flex flex-col !pb-4 overflow-hidden">
      <div className="panel-page-header flex-shrink-0">
        <div className="min-w-0">
          <h1 className="panel-page-title">
            {t('pages.tenants.title')}
          </h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.tenants.subtitle')}
          </p>
        </div>
        {canAdminister && (
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
            {t('pages.tenants.create')}
          </Button>
        )}
      </div>

      {/* Filtros (colapsables) */}
      <div className="flex-shrink-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
              filtersOpen || activeFilterChips.length > 0
                ? 'border-teal-500/50 bg-teal-50 text-teal-800 dark:border-teal-400/40 dark:bg-teal-900/30 dark:text-teal-200'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700'
            }`}
            aria-expanded={filtersOpen}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M6 10h12M9 16h6" />
            </svg>
            {filtersOpen ? t('pages.tenants.hideFilters') : t('pages.tenants.showFilters')}
            {activeFilterChips.length > 0 && (
              <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-teal-600 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white">
                {activeFilterChips.length}
              </span>
            )}
            <svg
              className={`h-3.5 w-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {!filtersOpen && activeFilterChips.length > 0 && (
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={chip.onRemove}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                  title={chip.label}
                >
                  <span className="truncate">{chip.label}</span>
                  <span className="text-sm leading-none" aria-hidden>×</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {filtersOpen && (
          <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
              <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-[minmax(18rem,1.4fr)_minmax(9rem,0.7fr)_minmax(14rem,1fr)_minmax(11rem,0.85fr)]">
                <div>
                  <Input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('pages.tenants.searchPlaceholder')}
                    className="h-8 py-1 text-xs"
                    aria-label={t('common.search')}
                    title={t('common.search')}
                  />
                </div>
                <div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                    className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    aria-label={t('pages.tenants.statusFilter')}
                    title={t('pages.tenants.statusFilter')}
                  >
                    <option value="all">{t('pages.tenants.all')}</option>
                    <option value="active">{t('pages.tenants.active')}</option>
                    <option value="inactive">{t('pages.tenants.inactive')}</option>
                  </select>
                </div>
                <div>
                  <Input
                    type="text"
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                    placeholder={t('pages.tenants.emailFilterPlaceholder')}
                    className="h-8 py-1 text-xs"
                    aria-label={t('pages.tenants.emailFilter')}
                    title={t('pages.tenants.emailFilter')}
                  />
                </div>
                <div>
                  <select
                    value={expiryFilter}
                    onChange={(e) => setExpiryFilter(e.target.value as typeof expiryFilter)}
                    className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    aria-label={t('pages.tenants.expiryFilter')}
                    title={t('pages.tenants.expiryFilter')}
                  >
                    <option value="all">{t('pages.tenants.all')}</option>
                    <option value="expired">{t('pages.tenants.expired')}</option>
                    <option value="expires_soon">{t('pages.tenants.expiresSoon')}</option>
                    <option value="expires_next_month">{t('pages.tenants.expiresNextMonth')}</option>
                  </select>
                </div>
              </div>

              <button
                type="button"
                onClick={clearFilters}
                disabled={activeFilterChips.length === 0}
                className="inline-flex h-8 w-full items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white xl:w-8"
                title={t('pages.tenants.clearFilters')}
                aria-label={t('pages.tenants.clearFilters')}
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-8 0h10" />
                </svg>
                <span className="ml-2 text-sm xl:hidden">{t('pages.tenants.clearFilters')}</span>
              </button>
            </div>

            {activeFilterChips.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {activeFilterChips.map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={chip.onRemove}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                    title={chip.label}
                  >
                    <span className="truncate">{chip.label}</span>
                    <span className="text-sm leading-none" aria-hidden>×</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista mobile / tabla desktop */}
      <div className="flex-1 min-h-0 min-w-0">
        {/* Cards mobile */}
        <div className="md:hidden h-full overflow-y-auto space-y-2 panel-safe-bottom">
          {filteredTenants.length === 0 ? (
            <div className="panel-card px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('pages.tenants.noResults')}
            </div>
          ) : (
            filteredTenants.map((tenant) => {
              const initial = (tenant.nombre.trim().charAt(0) || '?').toUpperCase();
              return (
              <div
                key={tenant.id}
                className="panel-list-card px-3.5 py-3"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-sm font-bold text-slate-700 dark:bg-gray-700 dark:text-slate-200">
                    {initial}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[15px] font-semibold leading-snug text-slate-900 dark:text-white" title={tenant.nombre}>
                          {tenant.nombre}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400" title={tenant.emailAdministrador}>
                          {tenant.emailAdministrador}
                        </p>
                      </div>
                      <span className={`panel-chip flex-shrink-0 ${getActivoStatusBadgeClass(tenant.activo)}`}>
                        {tenant.activo ? t('pages.tenants.active') : t('pages.tenants.inactive')}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
                      <span className="rounded-md bg-slate-50 px-1.5 py-0.5 font-medium text-slate-600 dark:bg-gray-900/50 dark:text-slate-300">
                        {getPlanName(tenant.planAdminId)}
                      </span>
                      <span aria-hidden>·</span>
                      <span>{new Date(tenant.fechaActivoUTC).toLocaleDateString('es-AR')}</span>
                    </div>
                    {canAdminister && (
                      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-1 border-t border-slate-100 pt-2 dark:border-gray-700/80">
                        <button
                          type="button"
                          onClick={() => setEditingTenant(tenant)}
                          className="inline-flex h-8 items-center rounded-full border border-slate-200 bg-white px-2.5 text-[11px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                          title={t('common.edit')}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNotifySuspension(true);
                            setStatusChangeTenant(tenant);
                          }}
                          className={`inline-flex h-8 items-center rounded-full border px-2.5 text-[11px] font-medium shadow-sm transition ${
                            tenant.activo
                              ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200'
                              : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                          }`}
                          title={tenant.activo ? t('pages.tenants.deactivate') : t('pages.tenants.activate')}
                        >
                          {tenant.activo ? t('pages.tenants.deactivate') : t('pages.tenants.activate')}
                        </button>
                        <ActionMenu
                          triggerTitle={t('common.actions')}
                          items={[
                            {
                              icon: '📋',
                              label: t('pages.tenants.paymentHistory'),
                              onClick: () => setHistoryTenant(tenant),
                            },
                            {
                              icon: '💰',
                              label: t('pages.tenants.goCollections'),
                              onClick: () =>
                                navigate(
                                  `/collections?q=${encodeURIComponent(tenant.nombre)}`
                                ),
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );})
          )}
        </div>

        {/* Tabla desktop */}
        <div className="hidden md:block h-full panel-table-wrap">
          <table className="min-w-full divide-y divide-slate-200 dark:divide-gray-700">
            <thead className="panel-thead sticky top-0 z-10">
              <tr>
                <th
                  className="px-3 py-3.5 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('nombre')}
                >
                  {t('pages.tenants.name')}{renderSortIcon('nombre')}
                </th>
                <th
                  className="px-3 py-3.5 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('emailAdministrador')}
                >
                  {t('pages.tenants.email')}{renderSortIcon('emailAdministrador')}
                </th>
                <th
                  className="px-3 py-3.5 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('activo')}
                >
                  {t('pages.tenants.status')}{renderSortIcon('activo')}
                </th>
                <th
                  className="hidden md:table-cell px-3 py-3.5 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('planAdminId')}
                >
                  {t('pages.tenants.plan')}{renderSortIcon('planAdminId')}
                </th>
                <th
                  className="hidden lg:table-cell px-3 py-3.5 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('fechaActivoUTC')}
                >
                  {t('pages.tenants.activeDate')}{renderSortIcon('fechaActivoUTC')}
                </th>
                <th className="px-3 py-3.5 text-right text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-slate-200 dark:divide-gray-700">
              {filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-slate-500 dark:text-slate-400">
                    {t('pages.tenants.noResults')}
                  </td>
                </tr>
              ) : (
                filteredTenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-slate-50 dark:hover:bg-gray-700">
                    <td className="px-3 py-3.5 text-sm font-medium text-slate-900 dark:text-white">
                      <span className="block truncate max-w-[14rem]" title={tenant.nombre}>{tenant.nombre}</span>
                    </td>
                    <td className="px-3 py-3.5 text-sm text-slate-500 dark:text-slate-400">
                      <span className="block truncate max-w-[16rem]" title={tenant.emailAdministrador}>{tenant.emailAdministrador}</span>
                    </td>
                    <td className="px-3 py-3.5">
                      <span className={`px-2 inline-flex max-w-full truncate text-xs leading-5 font-semibold rounded-full ${getActivoStatusBadgeClass(tenant.activo)}`}>
                        {tenant.activo ? t('pages.tenants.active') : t('pages.tenants.inactive')}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-3 py-3.5 text-sm text-slate-500 dark:text-slate-400">
                      <span className="block truncate" title={getPlanName(tenant.planAdminId)}>{getPlanName(tenant.planAdminId)}</span>
                    </td>
                    <td className="hidden lg:table-cell px-3 py-3.5 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {new Date(tenant.fechaActivoUTC).toLocaleDateString('es-AR')}
                    </td>
                    <td className="px-3 py-3.5 text-right text-sm font-medium">
                      <div className="flex justify-end items-center gap-1">
                        {canAdminister && <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingTenant(tenant)}
                          title={t('common.edit')}
                        >
                          ✏️
                        </Button>}
                        {canAdminister && <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNotifySuspension(true);
                            setStatusChangeTenant(tenant);
                          }}
                          title={tenant.activo ? t('pages.tenants.deactivate') : t('pages.tenants.activate')}
                        >
                          {tenant.activo ? '🔴' : '🟢'}
                        </Button>}
                        {canAdminister && <ActionMenu
                          triggerTitle={t('common.actions')}
                          items={[
                            {
                              icon: '📋',
                              label: t('pages.tenants.paymentHistory'),
                              onClick: () => setHistoryTenant(tenant),
                            },
                            {
                              icon: '💰',
                              label: t('pages.tenants.goCollections'),
                              onClick: () =>
                                navigate(
                                  `/collections?q=${encodeURIComponent(tenant.nombre)}`
                                ),
                            },
                          ]}
                        />}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error.message}</p>
        </div>
      )}

      {/* Modales */}
      {showCreateModal && (
        <CreateTenantModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}

      {editingTenant && (
        <EditTenantModal
          isOpen={!!editingTenant}
          tenant={editingTenant}
          onClose={() => setEditingTenant(null)}
          onSuccess={() => {
            setEditingTenant(null);
            refetch();
          }}
        />
      )}

      {historyTenant && (
        <PaymentHistoryModal
          isOpen={!!historyTenant}
          tenant={historyTenant}
          onClose={() => setHistoryTenant(null)}
        />
      )}

      {statusChangeTenant && (
        <ConfirmModal
          isOpen={!!statusChangeTenant}
          onClose={() => setStatusChangeTenant(null)}
          onConfirm={async () => {
            if (!statusChangeTenant) return;
            setIsChangingStatus(true);
            try {
              const wasActive = statusChangeTenant.activo;
              const action = container.get<ChangeTenantStatusAction>(TYPES.ChangeTenantStatusAction);
              await action.execute(statusChangeTenant.id);
              success(
                wasActive
                  ? t('pages.tenants.deactivatedSuccess')
                  : t('pages.tenants.activatedSuccess')
              );

              if (wasActive && notifySuspension) {
                try {
                  await httpClient.post('/collections/suspension-notice', {
                    tenantId: String(statusChangeTenant.id),
                    clinicName: statusChangeTenant.nombre,
                    toPhone: statusChangeTenant.telefonoContacto || null,
                  });
                  success(t('pages.tenants.suspensionNotified'));
                } catch (notifyErr) {
                  const axiosMsg =
                    notifyErr && typeof notifyErr === 'object' && 'response' in notifyErr
                      ? (notifyErr as { response?: { data?: { message?: string } } }).response
                          ?.data?.message
                      : undefined;
                  showError(
                    axiosMsg ||
                      (notifyErr instanceof Error
                        ? notifyErr.message
                        : t('pages.tenants.suspensionNotifyError'))
                  );
                }
              }

              setStatusChangeTenant(null);
              refetch();
            } catch (err) {
              showError(err instanceof Error ? err.message : t('common.error'));
            } finally {
              setIsChangingStatus(false);
            }
          }}
          title={statusChangeTenant.activo ? t('pages.tenants.deactivate') : t('pages.tenants.activate')}
          message={
            statusChangeTenant.activo 
              ? t('pages.tenants.deactivateConfirm', { name: statusChangeTenant.nombre })
              : t('pages.tenants.activateConfirm', { name: statusChangeTenant.nombre })
          }
          variant={statusChangeTenant.activo ? 'danger' : 'primary'}
          isLoading={isChangingStatus}
        >
          {statusChangeTenant.activo ? (
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                checked={notifySuspension}
                onChange={(e) => setNotifySuspension(e.target.checked)}
              />
              <span>
                {t('pages.tenants.notifySuspension')}
                {!statusChangeTenant.telefonoContacto ? (
                  <span className="block text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    {t('pages.tenants.notifySuspensionNoPhone')}
                  </span>
                ) : (
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {statusChangeTenant.telefonoContacto}
                  </span>
                )}
              </span>
            </label>
          ) : null}
        </ConfirmModal>
      )}
    </div>
  );
};
